<template>
  <div>
    <div class="p-schedule-visit__content__title">
      <span class="heading-4">{{ $t("visits.schedule.second.title") }}</span>
    </div>
    <v-expansion-panels accordion multiple flat>
      <v-expansion-panel ref="panel" class="p-schedule-visit__exp-panel p-schedule-visit__exp-panel__1">
        <v-expansion-panel-header class="p-schedule-visit__content__accordion-header">
          <span class="heading-5">
            {{ $t("visits.schedule.second.generalMedicalInformation") }}
            <span class="p-schedule-visit__content__required">*</span>
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content
          class="p-schedule-visit__content__accordion-content p-schedule-visit__content__accordion-content_general"
        >
          <v-row dense>
            <v-col>
              <BaseInput
                v-model="height"
                :placeholder="$t('general.inputs.height')"
                @change="onHeightChange"
                maskPattern="####"
              />
            </v-col>
            <v-col>
              <BaseInput
                v-model="weight"
                :placeholder="$t('general.inputs.weight')"
                @change="onWeightChange"
                maskPattern="####"
              />
            </v-col>
          </v-row>
          <span class="p-schedule-visit__content__accordion-content__smoking-status">
            {{ $t("settings.profile.smokingStatus") }}:
          </span>
          <Switcher :value="isSmoking" :items="$t('general.smokingStatus')" @change="onIsSmokingChange" />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel class="p-schedule-visit__exp-panel p-schedule-visit__exp-panel__2">
        <v-expansion-panel-header class="p-schedule-visit__content__accordion-header">
          <div class="p-schedule-visit__content__accordion-header__column">
            <span class="heading-5">{{ $t("visits.schedule.second.pastMedicalHistory") }}</span>
            <span class="sub-heading-1 p-schedule-visit__content__accordion-header__column__sub-header">
              {{ $t("visits.schedule.second.pastMedicalHistorySub") }}
            </span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="p-schedule-visit__content__accordion-content">
          <PatientHistoryTable
            tableClassName="scrollable-table table__no-border-radius"
            :items="pmh"
            place="checkout"
            id="PMH"
            @change="onPMHChange"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel class="p-schedule-visit__exp-panel p-schedule-visit__exp-panel__3">
        <v-expansion-panel-header class="p-schedule-visit__content__accordion-header">
          <div class="p-schedule-visit__content__accordion-header__column">
            <span class="heading-5">{{ $t("visits.schedule.second.allergies") }}</span>
            <span class="sub-heading-1 p-schedule-visit__content__accordion-header__column__sub-header">
              {{ $t("visits.schedule.second.allergiesSub") }}
            </span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="p-schedule-visit__content__accordion-content__allergies">
          <Combobox
            :items="allergies"
            @change="onAllergiesChange"
            :placeholder="$t('visits.schedule.second.allergiesPlaceholder')"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel class="p-schedule-visit__exp-panel p-schedule-visit__exp-panel__4">
        <v-expansion-panel-header class="p-schedule-visit__content__accordion-header">
          <div class="p-schedule-visit__content__accordion-header__column">
            <span class="heading-5">{{ $t("visits.schedule.second.medications") }}</span>
            <span class="sub-heading-1 p-schedule-visit__content__accordion-header__column__sub-header">
              {{ $t("visits.schedule.second.medicationsSub") }}
            </span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content
          class="p-schedule-visit__content__accordion-content p-schedule-visit__content__accordion-content_medications"
        >
          <MedicationTable
            tableClassName="scrollable-table table__no-border-radius table__expand-tr"
            :items="medicationsMap"
            place="checkout"
            @change="onMedicationChange"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <div class="p-schedule-visit__confirm">
      <v-checkbox
        :value="confirmedAccurace"
        dense
        :label="$t('visits.schedule.second.confirmAccuracy')"
        @change="onConfirmCheckboxChange"
      />
    </div>
    <div class="p-schedule-visit__content__btn-container">
      <PrimaryButton
        className="primary-btn__blue"
        :fullWidth="true"
        :text="$t(`visits.schedule.second.${price ? 'continuePayment' : 'continue'}`)"
        size="large"
        :disabled="buttonDisabled"
        :loading="loading"
        @onClick="submit"
      />
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import MedicationTable from "@/components/Medications/Table";
import PatientHistoryTable from "@/components/PatientHistoryTable/Table";
import BaseInput from "@/components/uikit/BaseInput";
import Combobox from "@/components/uikit/Combobox";
import PrimaryButton from "@/components/uikit/PrimaryButton";
import Switcher from "@/components/uikit/Switcher";

import { snackBarEventBus, snackBarEventName } from "../../../eventBuses/snackBar.eventBus";

export default {
  name: "PatientRequestVisitSecondStep",
  props: ["value", "mode", "price"],
  components: {
    BaseInput,
    Switcher,
    Combobox,
    PrimaryButton,
    MedicationTable,
    PatientHistoryTable,
  },
  data: () => ({
    loading: false,
  }),
  computed: {
    ...mapGetters("checkout", [
      "height",
      "weight",
      "isSmoking",
      "allergies",
      "medications",
      "pmh",
      "confirmedAccurace",
    ]),
    buttonDisabled() {
      return !this.height || !this.weight || !this.confirmedAccurace;
    },
    medicationsMap() {
      if (!this.medications) {
        return [];
      }
      return this.medications.map((el) => ({
        ...el,
        startDate: new Date(el.startDate),
      }));
    },
  },
  mounted() {
    this.$refs.panel.toggle();
  },
  methods: {
    ...mapMutations("checkout", [
      "setMedication",
      "setAllergies",
      "setPMH",
      "setIsSmoking",
      "setStringFieldByName",
      "setConfirmedAccurace",
    ]),
    ...mapActions("checkout", ["submitSecondStep", "submitEditMode"]),
    onHeightChange(value) {
      this.setStringFieldByName({ value, fieldName: "height" });
    },
    onWeightChange(value) {
      this.setStringFieldByName({ value, fieldName: "weight" });
    },
    onIsSmokingChange(value) {
      this.setIsSmoking(value);
    },
    onAllergiesChange(value) {
      this.setAllergies(value);
    },
    onMedicationChange(value) {
      this.setMedication(value);
    },
    onPMHChange(value) {
      this.setPMH(value);
    },
    onConfirmCheckboxChange(value) {
      this.setConfirmedAccurace(value);
    },
    async submit() {
      this.$gtag.event("click", { method: "Open Schedule Dialog" });
      try {
        this.loading = true;
        if (this.mode === "edit") {
          await this.submitEditMode();
        } else {
          await this.submitSecondStep();
        }
      } catch (err) {
        if (err && err.response && err.response.status === 409) {
          snackBarEventBus.$emit(snackBarEventName, {
            message: this.$t("general.errors.slotConflict"),
            type: "error",
          });
          this.$emit("slotConflict");
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
